import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import artieImage from "../../assets/images/artie.png";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { ArtieScoreData, Recommendation, TacticRecommendation } from "@/types/artie-score";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Check, X } from "lucide-react";
import { showTaskCompleteToast } from "@/components/ui/toast-v2";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuthStore } from "@/stores/authStore";
import { InviteCollaboratorDialog } from "@/components/InviteCollaboratorDialog";

interface ScoreStatusResponse {
  score_data: ArtieScoreData;
}

const ProjectDetailsV2Page: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuthStore();
  
  // All state declarations first
  const [activeTab, setActiveTab] = useState("new");
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [hasInitializedExpansion, setHasInitializedExpansion] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [showOpportunitiesDialog, setShowOpportunitiesDialog] = useState(false);
  const [showRecommendationPreferenceDialog, setShowRecommendationPreferenceDialog] = useState(false);
  const [userWantsRecommendations, setUserWantsRecommendations] = useState(true);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteTask, setInviteTask] = useState<Recommendation | TacticRecommendation | null>(null);
  
  // Ref to track if task completion toast has been shown to prevent duplicates
  const taskCompletionToastShown = useRef(false);

  // All queries - always called, but conditionally enabled
  const { data: project, isLoading: isLoadingProject } = useQuery({
    queryKey: ['project', projectId || 'no-id'],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is missing");
      }
      return apiClient.getProject(projectId);
    },
    enabled: !!projectId,
  });

  const { data: scoreStatus, isLoading, error } = useQuery<ScoreStatusResponse>({
    queryKey: ['artieScoreStatus', projectId || 'no-id'],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is missing");
      }
      return apiClient.getArtieScoreStatus(projectId) as Promise<ScoreStatusResponse>;
    },
    enabled: !!projectId,
  });

  const artieScore = scoreStatus?.score_data;
  const currentScore = artieScore?.artie_score || 0;
  
  const isOwner = project?.user_id === user?._id;
  const currentUserCollaborator = project?.collaborators?.find(c => c.user_id === user?._id);

  // Check if we should show recommendations based on score and user preference
  const shouldShowRecommendations = () => {
    if (currentScore >= 90) {
      return false; // Never show recommendations when score is 90+
    }
    if (currentScore >= 70 && !userWantsRecommendations) {
      return false; // Don't show if user opted out when score reached 70+
    }
    return true;
  };

  // Get all recommendations from the API and filter out hidden ones
  const allApiRecommendations: (Recommendation | TacticRecommendation)[] = (artieScore?.current_recommendations || []).filter(rec => {
    if (user?.role === "admin" && rec.is_hidden) return true;
    if (rec.is_hidden) return false;
    if (rec.status === 'declined') return false;
    
    // If user is owner, they see all tasks
    if (isOwner) return true;
    
    // If user is collaborator, they only see tasks they were invited to
    if (currentUserCollaborator) {
      const isV2Tactic = 'tactic_id' in rec;
      
      // The backend stores task NAMES in collaborator.task_ids, not IDs
      // So we need to match by task name
      const taskName = isV2Tactic
        ? (rec as TacticRecommendation).tactic_name
        : (rec as Recommendation).what_to_improve;
      
      const collaboratorTaskIds = currentUserCollaborator.task_ids || [];
      
      if (collaboratorTaskIds.length > 0) {
        // Match by task name (what's actually stored in the database)
        return collaboratorTaskIds.includes(taskName);
      }
      
      // Collaborator has no specific task_ids assigned
      return false;
    }
    
    // Fallback for other users (admins, etc.)
    return true;
  });

  // Filter recommendations based on score and user preference
  const allRecommendations: (Recommendation | TacticRecommendation)[] = shouldShowRecommendations()
    ? allApiRecommendations
    : allApiRecommendations.filter(rec => rec.status === 'done');

  // Helper function to get priority value from either recommendation type
  const getPriority = (rec: Recommendation | TacticRecommendation): number => {
    if ('priority_rank' in rec) {
      return rec.priority_rank || 0;
    }
    if ('priority' in rec) {
      return rec.priority || 0;
    }
    return 0;
  };

  // "New" queue: top 3 unactioned (generated) items by priority_rank.
  // accepted / in_progress items live in their own tabs and do NOT occupy a New slot,
  // so moving a task to "Save for later" or "Get started" frees a slot for the next ranked item.
  const newQueueRecommendations = allRecommendations
    .filter(rec => !rec.status || rec.status === 'generated')
    .sort((a, b) => getPriority(a) - getPriority(b))
    .slice(0, 3);

  // Build the full list used by renderTaskList — each tab filters by its own status
  const recommendations: (Recommendation | TacticRecommendation)[] = [
    ...newQueueRecommendations,
    ...allRecommendations.filter(rec => rec.status === 'accepted'),
    ...allRecommendations.filter(rec => rec.status === 'in_progress'),
    ...allRecommendations.filter(rec => rec.status === 'done'),
  ];

  // Helper functions to handle both V2 and legacy recommendations
  const getTaskId = (task: Recommendation | TacticRecommendation, index: number) => task.id || `task-${index}`;
  const getTaskName = (task: Recommendation | TacticRecommendation) => {
    const isV2Tactic = 'tactic_id' in task;
    return isV2Tactic
      ? (task as TacticRecommendation).tactic_name
      : (task as Recommendation).what_to_improve;
  };
  const getTaskDescription = (task: Recommendation | TacticRecommendation, field: 'why' | 'how') => {
    const isV2Tactic = 'tactic_id' in task;
    if (isV2Tactic) {
      const tacticTask = task as TacticRecommendation;
      return field === 'why' ? tacticTask.gap_description : tacticTask.tactic_description;
    } else {
      const legacyTask = task as Recommendation;
      return field === 'why' ? legacyTask.why_it_matters : legacyTask.how_to_improve;
    }
  };
  const getTaskMetric = (task: Recommendation | TacticRecommendation) => {
    const isV2Tactic = 'tactic_id' in task;
    return isV2Tactic
      ? (task as TacticRecommendation).dimension // Show dimension for V2 tactics (reach, brand_fit, production_readiness)
      : (task as Recommendation).related_metric;
  };

  // All useEffect hooks - ensure they're always called in the same order
  // Load user preference from localStorage
  useEffect(() => {
    if (projectId) {
      const savedPreference = localStorage.getItem(`recommendations-preference-${projectId}`);
      if (savedPreference !== null) {
        setUserWantsRecommendations(savedPreference === 'true');
      }
    }
  }, [projectId]);

  // Set edited title when project loads
  useEffect(() => {
    if (project?.title) {
      setEditedTitle(project.title);
    }
  }, [project?.title]);

  // Handle task completed toast
  useEffect(() => {
    if (location.state?.taskCompleted) {
      const taskId = location.state.taskId;
      const sessionKey = `task-completed-toast-${projectId}-${taskId}`;
      const alreadyShown = sessionStorage.getItem(sessionKey);
      
      console.log("🐛 [DEBUG] Task completion toast check:", {
        taskCompleted: location.state.taskCompleted,
        taskId: taskId,
        sessionKey: sessionKey,
        alreadyShown: !!alreadyShown,
        refAlreadyShown: taskCompletionToastShown.current,
        timestamp: new Date().toISOString()
      });
      
      if (!alreadyShown && !taskCompletionToastShown.current) {
        console.log("🐛 [DEBUG] Showing task completion toast");
        
        // Mark as shown in both ref and sessionStorage
        taskCompletionToastShown.current = true;
        sessionStorage.setItem(sessionKey, 'true');
        
        showTaskCompleteToast();
        
        // Clear the state immediately to prevent duplicate notifications
        const newState = { ...location.state };
        delete newState.taskCompleted;
        delete newState.taskId;
        window.history.replaceState(newState, document.title);
      } else {
        console.log("🐛 [DEBUG] Skipping duplicate toast - already shown");
        
        // Still clear the state even if we don't show the toast
        const newState = { ...location.state };
        delete newState.taskCompleted;
        delete newState.taskId;
        window.history.replaceState(newState, document.title);
      }
    }
  }, [location.state?.taskCompleted, projectId]);

  // Cleanup old task completion toast flags on component mount
  useEffect(() => {
    const cleanupOldToastFlags = () => {
      const keys = Object.keys(sessionStorage);
      const taskCompletionKeys = keys.filter(key => key.startsWith('task-completed-toast-'));
      
      // Keep only the last 10 task completion flags to prevent memory buildup
      if (taskCompletionKeys.length > 10) {
        const sortedKeys = taskCompletionKeys.sort();
        const keysToRemove = sortedKeys.slice(0, sortedKeys.length - 10);
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
      }
    };

    cleanupOldToastFlags();
  }, []);

  // Initialize expansion state
  useEffect(() => {
    if (recommendations.length > 0 && !hasInitializedExpansion) {
      setExpandedTaskId(recommendations[0].id || '0');
      setHasInitializedExpansion(true);
    }
  }, [recommendations.length, hasInitializedExpansion]);

  // Check if we should show the recommendation preference dialog
  useEffect(() => {
    if (projectId && currentScore >= 70 && currentScore < 90) {
      const hasAskedBefore = localStorage.getItem(`recommendations-asked-${projectId}`);
      if (!hasAskedBefore && userWantsRecommendations) {
        setShowRecommendationPreferenceDialog(true);
      }
    }
  }, [currentScore, projectId, userWantsRecommendations]);

  const handleSaveTitle = async () => {
    if (!projectId || !editedTitle.trim()) return;

    try {
      await apiClient.updateProject(projectId, { title: editedTitle });
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setIsEditingTitle(false);
    } catch (error) {
      console.error("Failed to update project title", error);
    }
  };

  const handleCancelTitle = () => {
    setEditedTitle(project?.title || "");
    setIsEditingTitle(false);
  };

  const handleTaskClick = (taskId: string) => {
    if (expandedTaskId === taskId) {
      setExpandedTaskId(null);
    } else {
      setExpandedTaskId(taskId);
    }
  };

  const handleSaveForLater = async (task: Recommendation | TacticRecommendation, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent toggling the accordion
    if (!projectId) return;

    try {
      const isV2Tactic = 'tactic_id' in task;
      const recId = task.id || (isV2Tactic ? (task as TacticRecommendation).tactic_id : (task as Recommendation).what_to_improve);

      if (isV2Tactic) {
        await apiClient.updateTacticRecommendation(projectId, recId, {
          recommendation_id: recId,
          status: 'accepted',
        });
      } else {
        await apiClient.updateRecommendation(projectId, {
          recommendation_id: recId,
          status: 'accepted',
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['artieScoreStatus', projectId] });
    } catch (error) {
      console.error("Failed to save for later", error);
    }
  };

  const handleReject = async (task: Recommendation | TacticRecommendation, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent toggling the accordion
    if (!projectId) return;

    try {
      const isV2Tactic = 'tactic_id' in task;
      const recId = task.id || (isV2Tactic ? (task as TacticRecommendation).tactic_id : (task as Recommendation).what_to_improve);

      if (isV2Tactic) {
        await apiClient.updateTacticRecommendation(projectId, recId, {
          recommendation_id: recId,
          status: 'declined',
        });
      } else {
        await apiClient.updateRecommendation(projectId, {
          recommendation_id: recId,
          status: 'declined',
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['artieScoreStatus', projectId] });
    } catch (error) {
      console.error("Failed to reject recommendation", error);
    }
  };

  const handleRecommendationPreference = (wantsRecommendations: boolean) => {
    if (!projectId) return;

    setUserWantsRecommendations(wantsRecommendations);
    localStorage.setItem(`recommendations-preference-${projectId}`, wantsRecommendations.toString());
    localStorage.setItem(`recommendations-asked-${projectId}`, 'true');
    setShowRecommendationPreferenceDialog(false);
  };

  const handleGetStarted = async (task: Recommendation | TacticRecommendation, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!projectId) return;

    // Navigate to chatbot - status will be updated when user actually engages
    navigate(`/project-v2/chatbot/${projectId}`, {
      state: { recommendation: task }
    });
  };

  const handleOpenInviteDialog = (task: Recommendation | TacticRecommendation, e: React.MouseEvent) => {
    e.stopPropagation();
    setInviteTask(task);
    setIsInviteDialogOpen(true);
  };

  const handleCollaboratorInvited = async (email: string) => {
    if (!projectId || !inviteTask) return;

    try {
      const isV2Tactic = "tactic_id" in inviteTask;
      const taskId =
        inviteTask.id ||
        (isV2Tactic
          ? (inviteTask as TacticRecommendation).tactic_id
          : (inviteTask as Recommendation).what_to_improve);

      if (!taskId) return;

      if (isV2Tactic) {
        await apiClient.updateTacticRecommendation(projectId, taskId, {
          recommendation_id: taskId,
          status: "in_progress",
          assigned_to: email,
        });
      } else {
        await apiClient.updateRecommendation(projectId, {
          recommendation_id: taskId,
          status: "in_progress",
          assigned_to: email,
        });
      }

      await queryClient.invalidateQueries({ queryKey: ["artieScoreStatus", projectId] });
    } catch (error) {
      console.error("Failed to update task status after inviting collaborator", error);
    }
  };

  const getTasksByStatus = (status: Recommendation['status']) => recommendations.filter(task => task.status === status);

  const counts = {
    new: newQueueRecommendations.length,
    saved: getTasksByStatus('accepted').length,
    collaborating: getTasksByStatus('in_progress').length,
    completed: getTasksByStatus('done').length,
  };

  const renderTaskList = (status: Recommendation['status']) => {
    const currentTasks = getTasksByStatus(status);

    if (isLoading) {
      return <Skeleton className="h-48" />;
    }
    if (error) {
      return <div className="text-center py-12 text-red-500">Error: {error.message}</div>;
    }
    if (currentTasks.length === 0) {
      return <div className="text-center py-12 text-gray-500">No {status} tasks yet.</div>;
    }

    return (
      <div className="space-y-4">
        {currentTasks.map((task, index) => {
          const taskId = task.id || String(index);
          const isExpanded = expandedTaskId === taskId;

          return (
            <div
              key={taskId}
              className={`transition-all duration-300 ease-in-out cursor-pointer ${isExpanded
                ? "border-2 border-gray-400 rounded-2xl p-6 md:p-8 bg-white relative overflow-hidden"
                : "border border-gray-100 rounded-2xl p-6 bg-white flex justify-between items-center hover:border-gray-200"
                }`}
              onClick={() => handleTaskClick(taskId)}
            >
              {isExpanded ? (
                <>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex gap-4 items-start">
                      <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-bold flex-shrink-0 mt-1">{index + 1}</div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{getTaskName(task)}</h3>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="secondary" className="bg-gray-100 text-gray-600 hover:bg-gray-100 font-normal border-0 text-[10px] uppercase tracking-wide px-2">{getTaskMetric(task)}</Badge>
                          {user?.role === "admin" && task.is_hidden && (
                            <Badge variant="destructive" className="text-[10px] uppercase tracking-wide px-2">Hidden</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pl-12 mb-8">
                    <p className="text-sm text-gray-500 mb-3">Why it matters</p>
                    <p className="text-sm text-gray-600">{getTaskDescription(task, 'why')}</p>
                  </div>
                  <div className="pl-12 mb-8">
                    <p className="text-sm text-gray-500 mb-3">How to improve</p>
                    <p className="text-sm text-gray-600">{getTaskDescription(task, 'how')}</p>
                  </div>
                  {task.status !== 'done' && (
                    <div className="pl-12 flex flex-wrap gap-3">
                      <Button
                        className="bg-[#1a1a1a] text-white hover:bg-black rounded-lg h-10 px-6 font-normal"
                        onClick={(e) => handleGetStarted(task, e)}
                      >
                        {task.status === 'in_progress' ? 'Continue working' : 'Get started'}
                      </Button>
                      {project?.user_id === user?._id && (
                        <Button
                          variant="outline"
                          className="bg-white hover:bg-gray-50 text-[#1a1a1a] border-gray-200 rounded-lg h-10 px-6 font-normal"
                          onClick={(e) => handleOpenInviteDialog(task, e)}
                        >
                          Invite someone to help
                        </Button>
                      )}
                      {task.status !== 'accepted' && task.status !== 'in_progress' && (
                        <>
                          <Button
                            variant="outline"
                            className="bg-white hover:bg-gray-50 text-[#1a1a1a] border-gray-200 rounded-lg h-10 px-6 font-normal"
                            onClick={(e) => handleSaveForLater(task, e)}
                          >
                            Save for later
                          </Button>
                          <Button
                            variant="outline"
                            className="bg-white hover:bg-red-50 text-red-600 border-red-200 rounded-lg h-10 px-6 font-normal"
                            onClick={(e) => handleReject(task, e)}
                          >
                            Not interested
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex gap-4 items-center">
                    <div className="w-8 h-8 rounded-full bg-gray-300 text-white flex items-center justify-center font-bold flex-shrink-0 text-sm">{index + 1}</div>
                    <div>
                      <h3 className="text-base font-bold text-gray-800">{getTaskName(task)}</h3>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[oklch(0.99_0_0)] font-sans text-slate-900 pb-20">
      {/* Top Navigation Bar */}
      <header className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex items-center bg-[oklch(0.99_0_0)]">
        <div className="flex items-center gap-3">
          <img src={artieImage} alt="Artie" className="w-10 h-10 object-contain" />
          <span className="font-cabinet text-2xl font-bold">artie</span>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        {/* Project Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div className="flex-1">
            {isEditingTitle ? (
              <div className="flex items-center gap-2 mb-1">
                <Input
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="text-3xl font-bold font-cabinet h-12 w-full md:w-[400px]"
                  autoFocus
                />
                <Button
                  size="icon"
                  className="h-10 w-10 bg-green-500 hover:bg-green-600 text-white rounded-full shrink-0"
                  onClick={handleSaveTitle}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-10 w-10 border-red-200 bg-red-50 hover:bg-red-100 text-red-500 rounded-full shrink-0"
                  onClick={handleCancelTitle}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <h1 className="text-3xl font-bold font-cabinet mb-1">{project?.title || "Project"}</h1>
            )}
            <p className="text-gray-500 text-sm">{project?.format || "Genre"} • {project?.episode_count || 1} Episode{(project?.episode_count || 1) !== 1 ? 's' : ''} • Updated {new Date(project?.updated_at || Date.now()).toLocaleDateString()}</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
              onClick={() => navigate(`/project-v2/script-feedback/${projectId}`, { state: { showContinueButton: false } })}
            >
              View Script Feedback
            </Button>
            <Button
              variant="outline"
              className="bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
              onClick={() => setIsEditingTitle(true)}
            >
              Edit
            </Button>
          </div>
        </div>

        {/* Info Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {/* Potential Sponsorship Value Card */}
          <Card className="lg:col-span-1 p-6 border-gray-100 shadow-sm bg-white rounded-2xl flex flex-col justify-between h-full">
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Potential Sponsorship Value</h3>
              <div className="text-4xl md:text-5xl font-bold font-cabinet mb-2 tracking-tight">
                {artieScore?.psv_calculation?.psv_final ? `$${artieScore.psv_calculation.psv_final.toLocaleString()}` : 'TBD'}
              </div>
              <p className="text-gray-500 text-sm mb-4 leading-relaxed max-w-sm">
                {artieScore?.psv_calculation?.total_opportunities
                  ? `We found a total of ${artieScore.psv_calculation.total_opportunities} opportunities across categories.`
                  : "Think of this as an early read on your story's commercial potential. We'll build on it together."}
              </p>
            </div>
            <div>
              <Button
                variant="outline"
                className="bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
                onClick={() => setShowOpportunitiesDialog(true)}
              >
                View Details
              </Button>
            </div>
          </Card>

          {/* Artie Score Card */}
          <Card className="lg:col-span-2 p-10 border-gray-100 shadow-sm bg-white rounded-2xl flex flex-col h-full">
            <h3 className="font-bold text-gray-900 mb-2">Artie Score</h3>

            <div className="flex-1 flex flex-col justify-center items-center w-full px-4">
              <div className="relative w-full max-w-lg mt-8 mb-4">
                {/* Score Indicator - Positioned dynamically */}
                <div
                  className="absolute top-0 -translate-x-1/2 -translate-y-[calc(100%-16px)] flex flex-col items-center z-20"
                  style={{ left: `${Math.min(artieScore?.artie_score || 0, 100)}%` }}
                >
                  {/* Large Black Circle with Score */}
                  <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center shadow-2xl relative z-20 mb-[-1px]">
                    <span className="text-[#a3e635] text-4xl font-bold tracking-tighter">
                      {artieScore?.artie_score || 0}
                    </span>
                  </div>

                  {/* Connector "Drop" Shape */}
                  <div className="-mt-8 relative z-10 flex flex-col items-center pointer-events-none">
                    <svg
                      width="60"
                      height="50"
                      viewBox="0 0 60 50"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-black fill-current"
                    >
                      <path d="M10 0 Q 30 45 30 45 Q 30 45 50 0 Z" />
                    </svg>
                    {/* Small Circle at the bottom (on the bar) */}
                    <div className="w-4 h-4 bg-black rounded-full mt-[-7px]"></div>
                  </div>
                </div>

                {/* Progress Bar Background */}
                <div className="h-4 w-full bg-gray-100 rounded-full relative">
                  <div
                    className="h-full bg-gradient-to-r from-yellow-300 to-lime-400 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${Math.min(artieScore?.artie_score || 0, 100)}%` }}
                  ></div>
                </div>

                {/* Labels */}
                <div className="flex justify-between text-[10px] md:text-xs font-medium text-gray-400 mt-4 uppercase tracking-widest px-1">
                  <span className={`w-[25%] text-center ${artieScore?.score_label === 'early_stage' ? 'text-black font-bold' : ''}`}>Early Stage</span>
                  <span className={`w-[50%] text-center ${artieScore?.score_label === 'on_track' ? 'text-black font-bold' : ''}`}>On Track</span>
                  <span className={`w-[25%] text-center ${artieScore?.score_label === 'marketplace_ready' ? 'text-black font-bold' : ''}`}>Marketplace Ready ({'>'}70)</span>
                </div>
              </div>
            </div>

            <div className="mt-4 text-gray-500 text-xs text-left pt-2">
              Each task you complete strengthens your project and improves its chances of reaching your goal.
            </div>
          </Card>
        </div>

        {/* Tasks Section - Always show if there are any recommendations */}
        {recommendations.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold font-cabinet mb-6">Tasks</h2>

            {shouldShowRecommendations() ? (
              <Tabs defaultValue="new" value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="w-full justify-between bg-transparent border-b border-gray-200 rounded-none h-auto p-0 mb-8">
                  <TabsTrigger
                    value="new"
                    className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:shadow-none bg-transparent px-0 pb-3 text-gray-500 data-[state=active]:text-black font-bold text-base hover:text-gray-800 transition-colors"
                  >
                    New <span className="ml-2 bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{counts.new}</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="saved"
                    className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:shadow-none bg-transparent px-0 pb-3 text-gray-500 data-[state=active]:text-black font-normal text-base hover:text-gray-800 transition-colors"
                  >
                    Saved for later <span className="ml-2 bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{counts.saved}</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="collaborating"
                    className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:shadow-none bg-transparent px-0 pb-3 text-gray-500 data-[state=active]:text-black font-normal text-base hover:text-gray-800 transition-colors"
                  >
                    Collaborating <span className="ml-2 bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{counts.collaborating}</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="completed"
                    className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:shadow-none bg-transparent px-0 pb-3 text-gray-500 data-[state=active]:text-black font-normal text-base hover:text-gray-800 transition-colors"
                  >
                    Completed <span className="ml-2 bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{counts.completed}</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="new">
                  {renderTaskList('generated')}
                </TabsContent>
                <TabsContent value="saved">
                  {renderTaskList('accepted')}
                </TabsContent>
                <TabsContent value="collaborating">
                  {renderTaskList('in_progress')}
                </TabsContent>
                <TabsContent value="completed">
                  {renderTaskList('done')}
                </TabsContent>
              </Tabs>
            ) : (
              // Show only completed tasks when recommendations are hidden
              <div className="space-y-4">
                <div className="text-center py-8 text-gray-500">
                  <h3 className="text-lg font-semibold mb-2">🎉 Congratulations!</h3>
                  <p className="mb-4">Your project has reached an excellent score of {currentScore}. You're doing great!</p>
                  {currentScore >= 90 ? (
                    <p className="text-sm">Your project is marketplace ready - no more recommendations needed.</p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm">You've chosen to focus on your current progress.</p>
                      <Button
                        variant="outline"
                        className="text-sm"
                        onClick={() => handleRecommendationPreference(true)}
                      >
                        Show recommendations again
                      </Button>
                    </div>
                  )}
                </div>
                {counts.completed > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Your Completed Tasks</h3>
                    <div className="space-y-4">
                      {renderTaskList('done')}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Opportunities Dialog */}
      <Dialog open={showOpportunitiesDialog} onOpenChange={setShowOpportunitiesDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Sponsorship Opportunities Breakdown</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {artieScore?.psv_calculation ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Opportunities</p>
                    <p className="text-2xl font-bold">{artieScore.psv_calculation.total_opportunities || 0}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Potential Value</p>
                    <p className="text-2xl font-bold">${artieScore.psv_calculation.psv_final?.toLocaleString() || 'TBD'}</p>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-3">
                  <h4 className="font-semibold text-gray-900">Breakdown by Type</h4>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-700">Visible Logo Opportunities</span>
                    <Badge variant="secondary" className="bg-gray-100 text-gray-900">{artieScore.psv_calculation.logo_count || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-700">Product Mention Opportunities</span>
                    <Badge variant="secondary" className="bg-gray-100 text-gray-900">{artieScore.psv_calculation.mention_count || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-700">Product Use Opportunities</span>
                    <Badge variant="secondary" className="bg-gray-100 text-gray-900">{artieScore.psv_calculation.direct_use_count || 0}</Badge>
                  </div>
                </div>

                {artieScore.psv_calculation.calculation_notes && (
                  <div className="bg-gray-50 rounded-lg p-4 mt-4">
                    <p className="text-xs text-gray-600">{artieScore.psv_calculation.calculation_notes}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No opportunity data available yet.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Recommendation Preference Dialog */}
      <Dialog open={showRecommendationPreferenceDialog} onOpenChange={setShowRecommendationPreferenceDialog}>
        <DialogContent className="sm:max-w-[520px] max-w-[95vw]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">🎉 Congratulations on reaching {currentScore}!</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-gray-700 leading-relaxed">
              Your project is performing really well! You've reached the "Marketplace Ready" threshold.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Would you like to continue receiving task recommendations to further optimize your project, or would you prefer to focus on your current progress?
            </p>
            <div className="flex flex-col gap-3 pt-4 w-full">
              <Button
                className="w-full bg-black hover:bg-gray-800 text-white text-sm py-2 px-4"
                onClick={() => handleRecommendationPreference(true)}
              >
                Continue showing recommendations
              </Button>
              <Button
                variant="outline"
                className="w-full text-sm py-2 px-4"
                onClick={() => handleRecommendationPreference(false)}
              >
                Focus on current progress
              </Button>
            </div>
            <p className="text-xs text-gray-500 text-center pt-2">
              You can change this preference anytime from your project settings.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Collaborator Dialog */}
      <InviteCollaboratorDialog
        isOpen={isInviteDialogOpen}
        onClose={() => setIsInviteDialogOpen(false)}
        projectId={projectId || ''}
        task={inviteTask}
        onInvited={handleCollaboratorInvited}
      />
    </div>
  );
};

export default ProjectDetailsV2Page;